import { Dispatch, ReactNode, SetStateAction, useEffect, useRef } from 'react'
import { TMessageSchedule, TProgrammedMessage } from './programmed-message.type'
import { useTranslation } from 'react-i18next'
import {
  CopyIcon,
  CurecallButton,
  CurecallCopyButton,
  CurecallTag,
  CurecallTooltip,
  CurecallTooltipProps,
  ECurecallSize,
  EyeIcon,
  PenIcon,
  replaceApiSyntaxWithVariables,
  TrashIcon,
  TTextareaVariable,
  TTextareaVariableAddable,
  TVariableStore,
} from '@honestica/curecall-ui'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { getDateValuesFromSchedule } from './programmed-message.helper'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useLocale } from 'src/hooks/use-locale'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import { getNotEditableReasonLabel, isContentPartial } from './not-editable-message.helper'
import { useNotEditableBackofficeUrl } from './use-not-editable-backoffice-url.hook'

const Text = ({ children }: { children: ReactNode }) => (
  <p className="c-font-nexa c-text-xs c-m-0">{children}</p>
)

const TextCell = Text

const MedEventsCell = ({ medEventIds, isFirstLine }: { medEventIds: number[]; isFirstLine?: boolean }) => {
  const { t } = useTranslation()
  const { getMedEventLabelById } = useCurrentProject()

  const medEventsRenamed = medEventIds.map((medEventId) => ({
    id: medEventId,
    label: getMedEventLabelById(medEventId),
  }))

  return medEventsRenamed.length === 1 ? (
    <TextCell>{medEventsRenamed[0]?.label}</TextCell>
  ) : (
    <div className="c-flex">
      <CurecallTooltip
        content={medEventsRenamed.map((medEvent, index) => (
          <div key={`tooltip-${medEvent.id}-${index}`}>{getMedEventLabelById(medEvent.id)}</div>
        ))}
        tooltipPosition={isFirstLine ? 'bottom' : 'top'}
        contentWrapperClassName="tw-w-max"
      >
        <Text>
          {medEventsRenamed.length} {t('pages.configuration.messages.selected')}
        </Text>
      </CurecallTooltip>
    </div>
  )
}

const ProgrammedDateCell = ({ schedule }: { schedule: TMessageSchedule }) => {
  const { t } = useTranslation()
  const { isBefore, value, multiplicator } = getDateValuesFromSchedule(schedule)

  return (
    <>
      <Text>
        {t('pages.configuration.messages.programming-date-value', { value, multiplicator, isBefore })}
      </Text>
      {schedule.atTime && (
        <Text>{t('pages.configuration.messages.programming-date-hour', { hour: schedule.atTime })}</Text>
      )}
    </>
  )
}

const MessageContentWithCopyButton = ({
  programmedMessageId,
  messageContent,
  variableStore,
  contentVariables,
  defaultContentVariable,
  setVariableToShow,
  tooltipPosition,
}: {
  programmedMessageId: number
  messageContent: Partial<Record<string, string>>
  variableStore: TVariableStore
  contentVariables: TTextareaVariableAddable[]
  defaultContentVariable: TTextareaVariableAddable
  setVariableToShow: Dispatch<SetStateAction<TTextareaVariable | null>>
  tooltipPosition?: CurecallTooltipProps['tooltipPosition']
}) => {
  const paragraphRef = useRef<HTMLParagraphElement | null>(null)
  const { locale } = useLocale()
  const { t } = useTranslation()
  const messageTranslated = getTranslatedLabel(locale, messageContent)
  const messageWithHtml = replaceApiSyntaxWithVariables({
    messageContent: messageTranslated,
    variableStore,
    contentVariables,
    defaultContentVariable,
    onVariableClick: (variable: TTextareaVariable) => setVariableToShow(variable),
  })

  useEffect(() => {
    if (messageWithHtml.length && paragraphRef.current) {
      paragraphRef.current.replaceChildren(...messageWithHtml)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmedMessageId]) // programmedMessageId helps to update the message content cell when user use filter

  return (
    <div className="c-flex c-items-center">
      <CurecallTooltip
        tooltipPosition={tooltipPosition || 'top'}
        content={t('pages.configuration.messages.copy-message-content')}
        contentWrapperClassName="tw-w-max"
      >
        <CurecallCopyButton
          ariaLabelText={t('pages.configuration.messages.copy-message-content')}
          data={messageContent?.[locale] || ''}
          size={ECurecallSize.SMALL}
        />
      </CurecallTooltip>
      <div className="c-m-0 c-pl-1 c-font-nexa" ref={paragraphRef} />
    </div>
  )
}

const MessageContentCell = ({
  programmedMessage,
  variableStore,
  contentVariables,
  defaultContentVariable,
  setVariableToShow,
  setProgrammedMessageToShow,
}: {
  programmedMessage: TProgrammedMessage
  variableStore: TVariableStore
  contentVariables: TTextareaVariableAddable[]
  defaultContentVariable: TTextareaVariableAddable
  setVariableToShow: Dispatch<SetStateAction<TTextareaVariable | null>>
  setProgrammedMessageToShow: Dispatch<SetStateAction<TProgrammedMessage | null>>
}) => {
  const { t } = useTranslation()

  if (!programmedMessage.profileRules.length) return ''
  if (programmedMessage.profileRules.length === 1) {
    return (
      <div className="c-flex c-flex-col c-gap-1">
        {isContentPartial(programmedMessage.notEditableReason) && (
          <CurecallTooltip
            content={t('pages.configuration.messages.partial-content-warning')}
            tooltipPosition="top"
            contentWrapperClassName="tw-w-max"
          >
            <CurecallTag status="WARNING" className="tw-text-xs">
              {t('pages.configuration.messages.partial-content-tag')}
            </CurecallTag>
          </CurecallTooltip>
        )}
        <MessageContentWithCopyButton
          programmedMessageId={programmedMessage.id}
          messageContent={programmedMessage.profileRules[0].message}
          variableStore={variableStore}
          contentVariables={contentVariables}
          defaultContentVariable={defaultContentVariable}
          setVariableToShow={setVariableToShow}
        />
      </div>
    )
  }

  return (
    <div className="c-flex c-items-center">
      <CurecallTooltip
        tooltipPosition="top"
        content={t('pages.configuration.messages.see-all-messages')}
        contentWrapperClassName="tw-w-max"
      >
        <CurecallButton
          isGhostButton={true}
          ariaLabelText={t('pages.configuration.messages.see-all-messages')}
          iconType={<EyeIcon className="tw-w-5 tw-h-max tw-w-min" />}
          onClick={() => setProgrammedMessageToShow(programmedMessage)}
          size={ECurecallSize.SMALL}
          className="tw-text-primary tw-min-w-[28px]"
        />
      </CurecallTooltip>
      <p className="c-m-0 c-pl-1 c-font-nexa">
        {t('pages.configuration.messages.followup-contains', {
          numberOfMessages: programmedMessage.profileRules.length,
        })}
      </p>
    </div>
  )
}

const ActionsCell = ({
  programmedMessage,
  onDeleteMessage,
  onUpdateMessage,
  onDuplicateMessage,
}: {
  programmedMessage: TProgrammedMessage
  onDeleteMessage: VoidFunction
  onUpdateMessage: (id: number, followupId: number | null) => void
  onDuplicateMessage: VoidFunction
}) => {
  const { t } = useTranslation()
  const backofficeUrl = useNotEditableBackofficeUrl(programmedMessage)

  const canEditOrDuplicate = programmedMessage.isEditable && programmedMessage.followupId !== null
  const editTooltip = getNotEditableReasonLabel(t, programmedMessage.notEditableReason)

  return (
    <div className="c-flex c-justify-end c-gap-2">
      {canEditOrDuplicate && (
        <CurecallButton
          isGhostButton={true}
          ariaLabelText={t('pages.configuration.messages.duplicate.duplicate-message', {
            messageName: programmedMessage.messageName,
          })}
          iconType={<CopyIcon />}
          onClick={onDuplicateMessage}
          size={ECurecallSize.SMALL}
          className="tw-text-primary"
        />
      )}

      {backofficeUrl && (
        <CurecallTooltip
          content={t('pages.configuration.messages.see-in-backoffice')}
          tooltipPosition="left"
          wrapperClassName="[&>div]:c-h-full c-h-full"
        >
          <a
            href={backofficeUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('pages.configuration.messages.see-in-backoffice')}
            className="c-flex c-items-center c-justify-center c-h-full c-px-2"
          >
            <AdminOnlyBadge />
          </a>
        </CurecallTooltip>
      )}

      {canEditOrDuplicate ? (
        <CurecallButton
          isGhostButton={true}
          ariaLabelText={t('pages.configuration.messages.update-message', {
            messageName: programmedMessage.messageName,
          })}
          iconType={<PenIcon />}
          onClick={() => onUpdateMessage(programmedMessage.id, programmedMessage.followupId)}
          size={ECurecallSize.SMALL}
          className="tw-text-primary"
        />
      ) : (
        <CurecallTooltip content={editTooltip} tooltipPosition="left">
          <CurecallButton
            ariaLabelText={t('pages.configuration.messages.update-message', {
              messageName: programmedMessage.messageName,
            })}
            iconType={<PenIcon className="c-text-grey-600" />}
            size={ECurecallSize.SMALL}
            isDisabled={true}
            disabled={true}
          />
        </CurecallTooltip>
      )}

      {programmedMessage.isEditable ? (
        <CurecallButton
          isGhostButton={true}
          ariaLabelText={t('pages.configuration.messages.delete-message', {
            messageName: programmedMessage.messageName,
          })}
          iconType={<TrashIcon />}
          onClick={onDeleteMessage}
          size={ECurecallSize.SMALL}
          className="tw-text-grey-700"
        />
      ) : (
        <CurecallTooltip
          content={t('pages.configuration.messages.not-deletable')}
          tooltipPosition="left"
          wrapperClassName="[&>div]:c-h-full c-h-full"
        >
          <CurecallButton
            ariaLabelText={t('pages.configuration.messages.delete-message', {
              messageName: programmedMessage.messageName,
            })}
            iconType={<TrashIcon className="c-text-grey-600" />}
            size={ECurecallSize.SMALL}
            isDisabled={true}
            disabled={true}
          />
        </CurecallTooltip>
      )}
    </div>
  )
}

export {
  TextCell,
  MedEventsCell,
  ProgrammedDateCell,
  MessageContentCell,
  ActionsCell,
  MessageContentWithCopyButton,
}
